import { useLocation } from "react-router-dom";
import { useMemo } from "react";
import useMenuItems from "./useMenuItems";
import type { MenuItem, SubLink } from "@/types";

// Known acronym/brand-casing exceptions where naive title-casing gets the
// display name visibly wrong (the removed integration sublinks carried the
// correct label for these). Deliberately a small local map, not an import of
// Applications.ts's full 676-line data file, into this globally-used hook.
const SLUG_LABEL_OVERRIDES: Record<string, string> = {
  hubspot: "HubSpot",
  bigcommerce: "BigCommerce",
  woocommerce: "WooCommerce",
  quickbooks: "QuickBooks",
  servicenow: "ServiceNow",
  servicetitan: "ServiceTitan",
  callrail: "CallRail",
  opentable: "OpenTable",
  sap: "SAP",
  mcp: "MCP",
  ga4: "GA4",
  vast: "VAST",
  netsuite: "NetSuite",
  onedrive: "OneDrive"
};

/** "hubspot" -> "HubSpot", "square-appointments" -> "Square Appointments". Used
 *  only as a fallback leaf label for a no-sublinks item's own sub-routes (see
 *  the `else if` branch below) -- deliberately dependency-free (no importing a
 *  feature's full data file into this globally-used hook just for display
 *  labels) since this hook renders on every page. */
function humanizeSegment(segment: string): string {
  const override = SLUG_LABEL_OVERRIDES[segment.toLowerCase()];
  if (override) return override;
  return segment
    .split(/[-_]/)
    .filter(Boolean)
    .map(
      (s) =>
        SLUG_LABEL_OVERRIDES[s.toLowerCase()] ??
        s.charAt(0).toUpperCase() + s.slice(1)
    )
    .join(" ");
}

export interface Crumb {
  label: string;
  to?: string;
}

/**
 * A section's matched sublink can be effectively the section itself rather
 * than a distinct child within it - e.g. the "Rules" menu item's own default
 * sublink is also labeled "Rules" (same URL, same name), and "Integrations"'
 * default sublink "All Integrations" points at the section's own `to`. In
 * both cases there is only one real page, so a two-crumb trail just restates
 * its own name ("Rules › Rules") rather than showing real hierarchy - per
 * NN/g's guidance to omit breadcrumbs below ~2 meaningfully distinct levels
 * (nngroup.com/articles/breadcrumbs), and GOV.UK's guidance against
 * breadcrumbs that duplicate a sidebar already showing the current section.
 * Sections with genuinely distinct sibling pages (e.g. "AI Chatbot" ›
 * "AI Configurations", a sibling of "Bot Setup Wizard") are unaffected -
 * neither condition below holds for them.
 *
 * Only ever collapses on an EXACT match against the current path - a route
 * added later that merely prefix-matches the section's own sublink (e.g. a
 * future `/rules/:id`) must never collapse away its own breadcrumb. The
 * label-based branch additionally requires the section itself to have no
 * distinct `to` of its own - if it does, a coincidentally identical label on
 * an otherwise different sublink is not the same page and must not collapse.
 */
export function isSelfReferentialMatch(
  item: MenuItem,
  best: SubLink,
  path: string
): boolean {
  if (path !== best.to) return false;
  if (item.to !== undefined && item.to === best.to) return true;
  return (
    item.to === undefined &&
    item.label.trim().toLowerCase() === best.label.trim().toLowerCase()
  );
}

/**
 * Resolves the href for a GROUP-level parent crumb (a top-level menu item
 * that itself has no `to`, e.g. Settings, Deployment, Workflows, Reports,
 * Billing - only Integrations and Knowledge Base have a parent `to`).
 *
 * Never falls back to the current page's own path - that produces a dead
 * link (the SPA router sees no URL change, so clicking does nothing).
 * Preference order:
 *   1. The item's own `to`, if it's not the current page.
 *   2. A sibling sublink whose label looks like a "hub"/landing page for the
 *      section (e.g. "Settings Hub"), if it's not the current page.
 *   3. undefined - there is genuinely no distinct parent page to link to,
 *      so the caller renders plain (non-clickable) text instead of a
 *      same-URL link.
 */
function resolveParentHref(
  item: MenuItem,
  currentPath: string
): string | undefined {
  if (item.to && item.to !== currentPath) return item.to;

  const hub = item.sublinks?.find(
    (s) =>
      s.enabled === true &&
      s.to &&
      s.to !== currentPath &&
      /\bhub\b/i.test(s.label)
  );
  if (hub?.to) return hub.to;

  return undefined;
}

export function useBreadcrumb(): Crumb[] {
  const location = useLocation();
  const menuItems = useMenuItems();
  return useMemo(() => {
    const path = location.pathname;
    if (!path || path === "/") return [];
    const crumbs: Crumb[] = [];
    for (const item of menuItems) {
      if (!item.enabled) continue;
      if (item.sublinks) {
        // Respect `end`: an exact-match-only sublink (e.g. "All Integrations",
        // to="/integrations", end=true) must never prefix-match a route that
        // isn't actually in the menu (e.g. /integrations/callrail) - that
        // mislabels the trail with the wrong leaf crumb. `end` sublinks only
        // match their own exact path; everything else keeps prefix matching.
        const matches = item.sublinks.filter((sub) => {
          if (!sub.enabled) return false;
          if (path === sub.to) return true;
          if (sub.end) return false;
          return path.startsWith(sub.to + "/");
        });
        if (matches.length > 0) {
          const best = matches.sort(
            (a, b) => (b.to?.length ?? 0) - (a.to?.length ?? 0)
          )[0];
          if (isSelfReferentialMatch(item, best, path)) {
            // The matched sublink IS the section, not a distinct child of it -
            // one crumb, not "Section › Section". Breadcrumb still renders
            // this single crumb (plus the recent-pages clock) - see
            // MainLayout.tsx/Breadcrumb.tsx, which only hide the bar for a
            // genuinely empty trail, not a collapsed one.
            crumbs.push({ label: best.label, to: best.to });
          } else {
            crumbs.push({
              label: item.label,
              to: resolveParentHref(item, path)
            });
            crumbs.push({ label: best.label, to: best.to });
          }
          break;
        }
      } else if (
        item.to &&
        (path === item.to || path.startsWith(item.to + "/"))
      ) {
        // Segments remaining after the item's own `to` (e.g. for /integrations,
        // "/integrations/hubspot/overview" -> ["hubspot", "overview"]). A bare
        // trailing slash ("/integrations/") also lands here via startsWith, but
        // has zero remaining segments -- treated the same as path === item.to.
        const remaining = path.slice(item.to.length).split("/").filter(Boolean);
        crumbs.push({ label: item.label, to: item.to });
        if (remaining.length === 1) {
          // On one of this item's own sub-routes (e.g. /integrations/hubspot under
          // the "Integrations" item, which has no sublinks of its own to match
          // instead) -- push a real two-crumb trail so the breadcrumb bar renders
          // at all (Breadcrumb.tsx hides itself for a 1-crumb trail) and back
          // navigation has somewhere real to go, matching the sublinks branch above.
          crumbs.push({ label: humanizeSegment(remaining[0]), to: path });
        } else if (remaining.length > 1) {
          // A deeper nested sub-route (e.g. /integrations/hubspot/overview) --
          // the first segment names the actual integration/section, so it gets
          // its own crumb linking to that page; the last segment is the current
          // leaf and never links to itself.
          const sectionPath = `${item.to}/${remaining[0]}`;
          crumbs.push({
            label: humanizeSegment(remaining[0]),
            to: sectionPath !== path ? sectionPath : undefined
          });
          crumbs.push({
            label: humanizeSegment(remaining[remaining.length - 1]),
            to: path
          });
        }
        break;
      }
    }
    return crumbs;
  }, [location.pathname, menuItems]);
}
